import { PropsWithChildren, useMemo } from 'react'

import {
  Text,
  Badge,
  GreyGrey70,
  IndigoIndigo60,
  Link,
  Popover,
  Primary13Regular,
  Td,
  Tr,
  Flex
} from '@sequencehq/core-components'
import { PriceTableRow } from 'modules/Products/types'
import {
  toBillingFrequencyLabel,
  toProductStatusBadgeProps
} from '@sequencehq/utils'
import { useQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'

const ProductPriceVariantCustomerPopover = ({
  priceMetadata,
  children
}: PropsWithChildren<{
  priceMetadata: DashboardApi20240730.Prices.PriceMetadata
}>) => {
  return (
    <Popover
      position="top"
      header="Customers"
      maxHeight="400px"
      variant="table"
      content={priceMetadata.billingSchedules.map(schedule => (
        <Tr
          key={schedule.billingScheduleId}
          sx={{
            _hover: {
              cursor: 'default!important',
              bg: 'inherit!important'
            }
          }}
        >
          <Td
            display={'flex'}
            alignItems="center"
            justifyContent="space-between"
          >
            <span {...Primary13Regular} color={GreyGrey70}>
              {schedule.customerLegalName}
            </span>
            <Link.Unstyled
              to={`/billing-schedules/${schedule.billingScheduleId}`}
              style={{
                ...Primary13Regular,
                fontWeight: '600',
                color: IndigoIndigo60
              }}
              onClick={e => e.stopPropagation()}
            >
              View schedule
            </Link.Unstyled>
          </Td>
        </Tr>
      ))}
    >
      {children}
    </Popover>
  )
}

export const ProductPriceVariant = ({
  priceRow,
  onSelect
}: {
  priceRow: PriceTableRow
  onSelect: () => void
}) => {
  const metadataQuery = useQuery(dashboard20240730Client.getPriceMetadata, {
    id: priceRow.id
  })

  const priceRowFrequency = toBillingFrequencyLabel(priceRow.frequency)

  const priceMetadata = metadataQuery.data

  const renderCustomerName = useMemo(() => {
    if (!priceMetadata) {
      return <Td {...Primary13Regular}>-</Td>
    }

    if (priceMetadata?.billingSchedules?.length > 0) {
      if (priceMetadata?.billingSchedules.length === 1) {
        return (
          <Td>
            <Text {...Primary13Regular} color={GreyGrey70}>
              {priceMetadata.billingSchedules[0].customerLegalName}
            </Text>
          </Td>
        )
      } else {
        return (
          <Td>
            <Flex gap="6px">
              <Text {...Primary13Regular} color={GreyGrey70}>
                {priceMetadata?.billingSchedules[0].customerLegalName}
              </Text>

              <ProductPriceVariantCustomerPopover priceMetadata={priceMetadata}>
                <Text
                  {...Primary13Regular}
                  fontWeight="600"
                  color={GreyGrey70}
                  borderBottom="1px dotted"
                  borderColor={GreyGrey70}
                >{`+ ${priceMetadata?.billingSchedules.length - 1}`}</Text>
              </ProductPriceVariantCustomerPopover>
            </Flex>
          </Td>
        )
      }
    }
  }, [priceMetadata])

  if (metadataQuery.isPending) {
    return null
  }

  return (
    <Tr
      key={priceRow.id}
      data-testid={`productPrice.variant.${priceRow.id}`}
      onClick={onSelect}
    >
      <Td>{priceRow.unitPrice}</Td>
      {renderCustomerName}
      <Td>{priceRow.currency}</Td>
      <Td>{priceRowFrequency}</Td>
      <Td>{priceRow.pricingModel}</Td>
      <Td>
        <Badge {...toProductStatusBadgeProps({ status: priceRow.status })} />
      </Td>
      <Td>{priceRow.created}</Td>
    </Tr>
  )
}
